/**
 * ActiveUsersPage — admin view of users by real last-activity (touched by
 * the JWT filter heartbeat). Summary cards (active now / today / this week /
 * never seen) + a paginated table with CSV/PDF export, plus device / browser
 * / OS / location (IP) breakdowns like the Strishakti superadmin active-users
 * tab.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Activity, Download, FileText, ChevronLeft, ChevronRight, Clock, Monitor, Smartphone, Tablet } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { adminService, type AdminUserRow, type ActiveUsersOverview } from '@/services/adminService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

const WINDOWS = [
  { key: 'now', label: 'Active Now (15 min)' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'all', label: 'All (with activity)' },
] as const

type Tab = 'users' | 'devices' | 'location'

const roleStyles: Record<string, string> = {
  ADMIN: 'bg-blue-100 text-blue-700',
  SUPER_ADMIN: 'bg-red-100 text-red-700',
  USER: 'bg-gray-100 text-gray-700',
}

const deviceMeta: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  mobile: { icon: Smartphone, color: 'text-pink-600' },
  tablet: { icon: Tablet, color: 'text-purple-600' },
  desktop: { icon: Monitor, color: 'text-blue-600' },
  unknown: { icon: Monitor, color: 'text-gray-400' },
}

const ActiveUsersPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [rows, setRows] = useState<AdminUserRow[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [windowSel, setWindowSel] = useState<typeof WINDOWS[number]['key']>('today')
  const [counts, setCounts] = useState({ activeNow: 0, activeToday: 0, activeThisWeek: 0, neverActive: 0 })
  const [overview, setOverview] = useState<ActiveUsersOverview | null>(null)
  const [tab, setTab] = useState<Tab>('users')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) return navigate('/login')
    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') navigate('/feed')
  }, [user, isAuthenticated, navigate])

  const fetch = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const [data, ov] = await Promise.all([
        adminService.getActiveUsers(windowSel, page, 20),
        adminService.getActiveUsersOverview().catch(() => null),
      ])
      setRows(data.users.content)
      setTotalPages(data.users.totalPages)
      setTotalElements(data.users.totalElements)
      setCounts({ activeNow: data.activeNow, activeToday: data.activeToday, activeThisWeek: data.activeThisWeek, neverActive: data.neverActive })
      if (ov) setOverview(ov)
    } catch (e) {
      console.error('fetch active users', e)
    } finally {
      setLoading(false)
    }
  }, [windowSel, page, user])

  useEffect(() => { fetch() }, [fetch])

  // Auto-refresh the device/location overview every 30s (like Strishakti).
  useEffect(() => {
    const t = setInterval(() => {
      adminService.getActiveUsersOverview().then((ov) => setOverview(ov)).catch(() => {})
    }, 30000)
    return () => clearInterval(t)
  }, [])

  const changeWindow = (w: typeof WINDOWS[number]['key']) => { setWindowSel(w); setPage(0) }

  const exportColumns: ExportColumn<AdminUserRow>[] = [
    { header: 'Name', key: 'fullName' },
    { header: 'Email', key: 'email' },
    { header: 'Role', key: 'role' },
    { header: 'User Type', key: 'userType' },
    { header: 'Status', key: 'status' },
    { header: 'Last Activity', key: 'lastActivity', format: (v) => (v ? new Date(v as string).toLocaleString() : '—') },
    { header: 'Last Login', key: 'lastLogin', format: (v) => (v ? new Date(v as string).toLocaleString() : '—') },
  ]

  const cards = [
    { label: 'Active Now', value: counts.activeNow, tone: 'text-green-600' },
    { label: 'Today', value: counts.activeToday, tone: 'text-blue-600' },
    { label: 'This Week', value: counts.activeThisWeek, tone: 'text-indigo-600' },
    { label: 'Never Seen', value: counts.neverActive, tone: 'text-gray-500' },
  ]

  const totalDevice = overview ? Object.values(overview.byDevice).reduce((a, b) => a + b, 0) : 0

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Active Users" subtitle="Real activity (last API request) — not just login time" icon={Activity} />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {cards.map((c) => (
            <GovCard key={c.label}>
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className={`text-2xl font-bold ${c.tone}`}>{c.value.toLocaleString()}</p>
            </GovCard>
          ))}
        </div>

        {/* Sub-tabs: Users / Devices / Location (like Strishakti) */}
        <div className="flex gap-2">
          {([
            { key: 'users', label: 'Users' },
            { key: 'devices', label: 'Devices' },
            { key: 'location', label: 'Location' },
          ] as const).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'users' && (
          <GovCard>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {WINDOWS.map((w) => (
                <button
                  key={w.key}
                  onClick={() => changeWindow(w.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${windowSel === w.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {w.label}
                </button>
              ))}
              <div className="ml-auto flex gap-2">
                <GovButton variant="secondary" size="sm" onClick={() => exportToCSV('active-users', exportColumns, rows)} disabled={!rows.length}>
                  <Download className="w-4 h-4 inline mr-1" /> CSV
                </GovButton>
                <GovButton variant="secondary" size="sm" onClick={() => exportToPDF('active-users', 'Active Users Report', exportColumns, rows)} disabled={!rows.length}>
                  <FileText className="w-4 h-4 inline mr-1" /> PDF
                </GovButton>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500 p-8 text-center">Loading…</p>
            ) : rows.length === 0 ? (
              <GovEmptyState icon="🟢" title="No Active Users" description="No users match this activity window yet. Browse the app to generate activity, or widen the window." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-4 text-sm font-semibold text-gray-600">User</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-600">Role</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-600">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((u) => (
                      <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="p-4 font-medium text-gray-900">{u.fullName || `${u.firstName} ${u.lastName}`}</td>
                        <td className="p-4 text-gray-700">{u.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleStyles[u.role] || roleStyles.USER}`}>{u.role}</span>
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          {u.lastActivity ? (
                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-gray-400" />{new Date(u.lastActivity).toLocaleString()}</span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                  <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </GovButton>
                  <span className="text-sm text-gray-600">Page {page + 1} of {Math.max(1, totalPages)} · {totalElements} total</span>
                  <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
                    Next <ChevronRight className="w-4 h-4" />
                  </GovButton>
                </div>
              </div>
            )}
          </GovCard>
        )}

        {tab === 'devices' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GovCard>
              <h3 className="font-bold text-gray-900 mb-3">By Device Type</h3>
              {!overview || totalDevice === 0 ? (
                <p className="text-sm text-gray-500">No active sessions in the last 15 minutes. Device data appears once authenticated users browse the app.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(overview.byDevice).sort((a, b) => b[1] - a[1]).map(([device, count]) => {
                    const meta = deviceMeta[device] || deviceMeta.unknown
                    const pct = totalDevice > 0 ? Math.round((count / totalDevice) * 100) : 0
                    return (
                      <div key={device}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="inline-flex items-center gap-2 capitalize text-gray-700"><meta.icon className={`w-4 h-4 ${meta.color}`} /> {device}</span>
                          <span className="font-medium text-gray-900">{count} · {pct}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </GovCard>

            <GovCard>
              <h3 className="font-bold text-gray-900 mb-3">By Browser</h3>
              <BreakdownList entries={overview ? Object.entries(overview.byBrowser) : []} emptyText="No browser data yet." />
            </GovCard>

            <GovCard className="md:col-span-2">
              <h3 className="font-bold text-gray-900 mb-3">By Operating System</h3>
              <BreakdownList entries={overview ? Object.entries(overview.byOs) : []} emptyText="No OS data yet." />
            </GovCard>
          </div>
        )}

        {tab === 'location' && (
          <GovCard>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">By IP Address</h3>
              <span className="text-xs text-gray-500">Top client IPs active in the last 15 minutes (location proxy)</span>
            </div>
            {!overview || overview.byIpAddress.length === 0 ? (
              <p className="text-sm text-gray-500">No active sessions in the last 15 minutes. IP data appears once authenticated users browse the app.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-4 text-sm font-semibold text-gray-600">IP Address</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-600">Active Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.byIpAddress.map((e) => (
                      <tr key={e.label} className="border-t border-gray-100">
                        <td className="p-4 font-medium text-gray-900">{e.label}</td>
                        <td className="p-4 text-gray-700">{e.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </GovCard>
        )}
      </div>
    </GovLayout>
  )
}

/** Vertical list of label → count entries with subtle bars. */
const BreakdownList: React.FC<{ entries: [string, number][]; emptyText: string }> = ({ entries, emptyText }) => {
  const total = entries.reduce((a, [, c]) => a + c, 0)
  if (total === 0) return <p className="text-sm text-gray-500">{emptyText}</p>
  return (
    <div className="space-y-2">
      {entries.sort((a, b) => b[1] - a[1]).map(([label, count]) => (
        <div key={label} className="flex items-center justify-between text-sm">
          <span className="text-gray-700">{label || 'Unknown'}</span>
          <span className="font-medium text-gray-900">{count}</span>
        </div>
      ))}
    </div>
  )
}

export default ActiveUsersPage